import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  getVendorReturnModalActions,
  updateVendorShopReturnStatus,
} from "../../utils/vendorShopOrders";

export default function ShopReturnActionModal({
  open,
  order,
  shopOrderId,
  onClose,
  onSuccess,
}) {
  const actionOptions = useMemo(
    () => (order ? getVendorReturnModalActions(order) : []),
    [order]
  );
  const [action, setAction] = useState("approved");
  const [vendorNote, setVendorNote] = useState("");
  const [loading, setLoading] = useState(false);

  const orderLabel = order?.orderId || shopOrderId || "—";

  useEffect(() => {
    if (!open) return;
    const first = actionOptions[0]?.id || "approved";
    setAction(first);
    setVendorNote("");
    setLoading(false);
  }, [open, shopOrderId, actionOptions]);

  if (!open) return null;

  if (!actionOptions.length) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="bg-white w-full max-w-md shadow-xl p-6 md:p-8">
          <p className="text-sm font-Montserratmedium text-[#555555]">
            No return actions are available for this order right now.
          </p>
          <div className="flex justify-end mt-4">
            <button
              type="button"
              onClick={() => onClose?.()}
              className="border border-[#2B2B2B] px-6 py-2 text-sm font-BonaNovabold"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async () => {
    const id = String(shopOrderId || order?.shopOrderId || order?.id || "").trim();
    if (!id || loading) return;
    if (action === "rejected" && !vendorNote.trim()) {
      toast.error("Enter a note for the customer");
      return;
    }

    setLoading(true);
    const result = await updateVendorShopReturnStatus(id, {
      status: action,
      vendorNote: vendorNote.trim(),
    });
    setLoading(false);

    if (result.ok) {
      toast.success(result.message || "Return updated");
      onSuccess?.();
      onClose?.();
      return;
    }
    toast.error(result.message || "Could not update return");
  };

  const canSubmit =
    action === "approved" ||
    action === "completed" ||
    (action === "rejected" && vendorNote.trim());

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 md:p-8">
          <h2 className="text-xl font-BonaNovabold text-black mb-2">
            Return request
          </h2>
          <p className="text-[12px] font-Montserratmedium text-[#555555] mb-6">
            Order: {orderLabel}
          </p>

          <p className="text-sm font-Montserratmedium text-[#555555] mb-3">
            Select status
          </p>
          <div className="space-y-2 mb-6">
            {actionOptions.map((opt) => (
              <label
                key={opt.id}
                className={`flex items-center gap-3 px-3 py-2 border cursor-pointer text-[13px] font-Montserratmedium ${
                  action === opt.id
                    ? "border-[#F4802C] bg-[#FFF8F3]"
                    : "border-[#E1E8D5] bg-white"
                }`}
              >
                <input
                  type="radio"
                  name="return-action"
                  value={opt.id}
                  checked={action === opt.id}
                  onChange={() => setAction(opt.id)}
                  disabled={loading}
                  className="accent-[#A4B792]"
                />
                {opt.label}
              </label>
            ))}
          </div>

          {action === "rejected" ? (
            <>
              <label
                htmlFor="return-vendor-note"
                className="block text-sm font-Montserratmedium text-[#555555] mb-2"
              >
                Vendor note
              </label>
              <textarea
                id="return-vendor-note"
                value={vendorNote}
                onChange={(e) => setVendorNote(e.target.value)}
                placeholder="Return rejected — not eligible"
                disabled={loading}
                rows={3}
                className="w-full border border-[#E1E8D5] rounded-none p-3 mb-6 text-sm font-Montserratmedium focus:outline-none focus:border-[#F4802C] resize-none"
              />
            </>
          ) : (
            <div className="mb-6" />
          )}

          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={() => !loading && onClose?.()}
              disabled={loading}
              className="border border-[#2B2B2B] px-6 py-2 text-sm font-BonaNovabold disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || !canSubmit}
              className="bg-[#A4B792] text-white px-6 py-2 text-sm font-BonaNovabold hover:bg-[#95a882] disabled:opacity-50"
            >
              {loading ? "Processing..." : "Confirm"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
